import { hasStorage, readStorage, STORAGE_KEYS, writeStorage } from "./storage";

const DEFAULT_BASE = "http://localhost:8000";

/**
 * The production CARTO basemap key, built into the bundle on purpose.
 *
 * A basemap key is public by design (it rides on every tile URL in the browser
 * and is restricted by referrer on the CARTO side), so shipping it here costs
 * nothing — and it is what lets a newspaper or partner embed the map with the
 * bare loader tag or the iframe page and never see a tile error. Nobody outside
 * this repo has to know the key exists.
 *
 * Rotating it: change this constant, merge to main. CI bumps the version and
 * promotes the new bundle to `prod/`; every embed (iframe pages load the bundle
 * relatively from their channel, script embeds load `prod/conny-widgets.js`)
 * picks the new key up on its next page load. No partner changes anything.
 *
 * The loader attribute / meta tag / global still override it (see
 * `getMapTileKey`), and the debug bar can switch keys off for the current tab.
 */
export const DEFAULT_MAP_TILE_KEY = "cb1_3334_1_c4ae270e1b093e4a704e8b47";

/**
 * Resolve a config value with the following precedence (highest first):
 *   1. sessionStorage override (set via the debug config bar)
 *   2. <script data-conny-...="..."> attribute on the loader tag
 *   3. <meta name="conny-..." content="..."> tag
 *   4. window.CONNY_CONFIG = { apiBase, apiToken, mapTileKey } global
 *   5. fallback default
 *
 * This lets the product team bake the base URL + token directly into the
 * Heyflow HTML block (via the script tag's data-attrs) while still letting
 * QA override them at runtime via the debug bar.
 */
function readFromLoaderScript(attr: string): string {
  if (typeof document === "undefined") return "";
  const tags = document.querySelectorAll<HTMLScriptElement>(
    "script[data-conny-loader], script[src*='conny-widgets']",
  );
  for (const tag of Array.from(tags)) {
    const v = tag.getAttribute(attr);
    if (v && v.trim()) return v.trim();
  }
  return "";
}

function readFromMeta(name: string): string {
  if (typeof document === "undefined") return "";
  const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  return (el?.content || "").trim();
}

function readFromGlobal(key: "apiBase" | "apiToken" | "mapTileKey"): string {
  const g = (
    typeof window !== "undefined"
      ? (window as unknown as { CONNY_CONFIG?: Record<string, string> })
      : {}
  ).CONNY_CONFIG;
  return (g?.[key] || "").trim();
}

export function getApiBase(): string {
  return (
    readStorage(STORAGE_KEYS.apiBase).trim() ||
    readFromLoaderScript("data-conny-api-base") ||
    readFromMeta("conny-api-base") ||
    readFromGlobal("apiBase") ||
    DEFAULT_BASE
  );
}
export function setApiBase(v: string): void {
  writeStorage(STORAGE_KEYS.apiBase, v);
}
export function getApiToken(): string {
  return (
    readStorage(STORAGE_KEYS.apiToken) ||
    readFromLoaderScript("data-conny-api-token") ||
    readFromMeta("conny-api-token") ||
    readFromGlobal("apiToken")
  );
}
export function setApiToken(v: string): void {
  writeStorage(STORAGE_KEYS.apiToken, v);
}

/**
 * Key for the map widgets' raster tile server (appended as `?key=` to every
 * tile request — see `shared/map-tiles.ts`). Same resolution chain as the API
 * token, ending in the key built into the bundle (`DEFAULT_MAP_TILE_KEY`) —
 * so an embed that configures nothing still gets attributed, keyed tiles.
 *
 * Unlike the token, a value saved from the debug bar wins even when it is "",
 * so QA can switch the key OFF for the current tab (keyless tiles) rather
 * than only replace it.
 */
export function getMapTileKey(): string {
  if (hasStorage(STORAGE_KEYS.mapTileKey)) {
    return readStorage(STORAGE_KEYS.mapTileKey).trim();
  }
  return (
    readFromLoaderScript("data-conny-map-tile-key") ||
    readFromMeta("conny-map-tile-key") ||
    readFromGlobal("mapTileKey") ||
    DEFAULT_MAP_TILE_KEY
  );
}
export function setMapTileKey(v: string): void {
  writeStorage(STORAGE_KEYS.mapTileKey, v);
}

function buildHeaders(): HeadersInit {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getApiToken();
  if (token) headers["X-Funnel-Key"] = token;
  return headers;
}

function buildUrl(
  path: string,
  params?: Record<string, string | undefined | null>,
): string {
  const base = getApiBase().replace(/\/+$/, "");
  if (!params) return base + path;
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `${base}${path}?${parts.join("&")}` : base + path;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  const resp = await fetch(url, {
    method: "GET",
    headers: buildHeaders(),
    credentials: "omit",
  });
  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error(`HTTP ${resp.status} on ${url}`);
  return (await resp.json()) as T;
}

// ---- Response types (mirror funnelboat/funnel_data/models.py) ----

export interface ZipStats {
  zip: string;
  cases: number;
  achieved_rent_per_sqm: number | null;
}

export interface AddressCases {
  house_cases: number;
  street_cases: number;
}

export interface Landlord {
  key: string;
  name: string;
  count: number | null;
  type: string;
}

export interface FunnelContext {
  zip: ZipStats | null;
  address: AddressCases | null;
  landlord: Landlord | null;
}

// ---- Endpoints ----

export function getFunnelContext(opts: {
  street?: string;
  house?: string;
  zip?: string;
}): Promise<FunnelContext | null> {
  const street = (opts.street || "").trim();
  const house = (opts.house || "").trim();
  const zip = (opts.zip || "").trim();
  if (!street && !house && !zip) {
    return Promise.resolve({ zip: null, address: null, landlord: null });
  }
  return fetchJson<FunnelContext>(
    buildUrl("/api/v1/public/funnel-context", { street, house, zip }),
  );
}

export function getZip(zip: string): Promise<ZipStats | null> {
  if (!zip) return Promise.resolve(null);
  return fetchJson<ZipStats>(
    buildUrl(`/api/v1/public/zip/${encodeURIComponent(zip)}`),
  );
}

export function listLandlords(): Promise<Landlord[]> {
  return fetchJson<Landlord[]>(buildUrl("/api/v1/public/landlords")).then(
    (d) => d || [],
  );
}

export function getLandlord(key: string): Promise<Landlord | null> {
  if (!key) return Promise.resolve(null);
  return fetchJson<Landlord>(
    buildUrl(`/api/v1/public/landlords/${encodeURIComponent(key)}`),
  );
}

// ---- Map (cases-on-map) ----

export interface MapCasePoint {
  street: string;
  house_number: string;
  zip_code: string;
  lat: number;
  lng: number;
  /** Sum of monthly savings across all closed cases at this address. */
  total: number;
  /** Total number of cases at this address. */
  cases: number;
  /** Cases with savings recorded (denominator for the average). */
  cws: number;
  closed: number;
  open: number;
  /** Map of landlord display name → case count at this address. */
  landlords: Record<string, number>;
  /** Years in which cases were closed. */
  years: number[];
}

export interface MapBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface MapDataset {
  city: string;
  points: MapCasePoint[];
  /** Optional bounding box the API recommends fitting on first render. */
  bbox: MapBounds | null;
}

export interface MapCasesQuery {
  city?: string;
  zip?: string;
  centerLat?: number;
  centerLng?: number;
  /** Radius around (centerLat, centerLng) in kilometres. */
  radiusKm?: number;
}

/**
 * Fetch the points to plot on the cases-on-map widget. The funnelboat API is
 * expected to filter server-side by city / ZIP / radius so the widget can
 * pull only what it needs.
 *
 * Callers that want a static fallback when the API is unreachable should
 * use `fetchMapCases()` in `cases-api.ts` instead of calling this directly.
 */
export function getMapCases(
  query: MapCasesQuery = {},
): Promise<MapDataset | null> {
  return fetchJson<MapDataset>(
    buildUrl("/api/v1/public/map/cases", {
      city: query.city,
      zip: query.zip,
      center_lat: query.centerLat != null ? String(query.centerLat) : undefined,
      center_lng: query.centerLng != null ? String(query.centerLng) : undefined,
      radius_km: query.radiusKm != null ? String(query.radiusKm) : undefined,
    }),
  );
}
